import { type ReactNode } from "react"
import { token } from "@octopus-energy/coral-design/tokens"
import { EmailLink, PhoneLink } from "@/components/atoms"
import {
    CUSTOMER_SERVICE_EMAIL,
    CUSTOMER_SERVICE_PHONE,
    type CustomerService,
    EMCE_CONTACT_EMAIL,
    EMCE_CUSTOMER_SERVICE_PHONE,
    FRAUD_CUSTOMER_SERVICE_PHONE,
} from "@/features/customer-service"

type Tags =
    | "br"
    | "b"
    | "i"
    | "sup"
    | "CustomerServicePhoneLink"
    | "CustomerServiceEmailLink"
    | "highlight"

type NextIntlRichTextProps = {
    customerService?: CustomerService
    children(tags: Record<Tags, (chunks: ReactNode) => ReactNode>): ReactNode
}

type CustomerServiceContact = {
    phone: string
    email: string
}

const CUSTOMER_SERVICE_CONTACT_MAP = {
    octopus: {
        phone: CUSTOMER_SERVICE_PHONE,
        email: CUSTOMER_SERVICE_EMAIL,
    },
    emce: {
        phone: EMCE_CUSTOMER_SERVICE_PHONE,
        email: EMCE_CONTACT_EMAIL,
    },
    fraud: {
        phone: FRAUD_CUSTOMER_SERVICE_PHONE,
        email: CUSTOMER_SERVICE_EMAIL,
    },
} satisfies Record<CustomerService, CustomerServiceContact>

// See https://next-intl-docs.vercel.app/docs/usage/messages#rich-text-reuse-tags for more details
export function NextIntlRichText({ customerService = "octopus", children }: NextIntlRichTextProps) {
    const customerServiceContact = CUSTOMER_SERVICE_CONTACT_MAP[customerService]

    return children({
        br: () => <br />,
        b: (chunks) => <b>{chunks}</b>,
        i: (chunks) => <i>{chunks}</i>,
        highlight: (chunks) => (
            <b style={{ color: token("colors.background.tertiary.500") }}>{chunks}</b>
        ),
        sup: (chunks) => <sup>{chunks}</sup>,
        CustomerServicePhoneLink: () => <PhoneLink phone={customerServiceContact.phone} />,
        CustomerServiceEmailLink: () => <EmailLink email={customerServiceContact.email} />,
    })
}
